import { ChangeEvent } from 'react';
import { Divider, Modal, ModalBody, ModalContent, ModalHeader, Tab, Tabs } from '@ds';
import { Form } from '@ui';
import { AttachmentsForm } from '@features/modals/CaseAttachmentsModal/types';
import { linksCaseSchema } from '@features/forms/CaseForms/schema';
import { CaseFile, CaseLinkType } from '@/api/apis/case';
import { FilesList } from './FilesList';
import { AttachmentLinkData, AttachmentLinkMutationResult, LinksList } from './LinksList';

export type AttachmentsFileUpload = {
  isUploading: boolean;
  onFileUpload: (e: ChangeEvent<HTMLInputElement>) => void;
  onDeleteFile: (fileId: string) => void;
};

export type AttachmentsLinkMutations = {
  addLink: (linkData: AttachmentLinkData) => Promise<AttachmentLinkMutationResult>;
  editLink: (linkId: string, linkData: AttachmentLinkData) => Promise<AttachmentLinkMutationResult>;
  deleteLink: (linkId: string) => Promise<AttachmentLinkMutationResult>;
  isPending: boolean;
};

const MODAL_CLASSNAMES = {
  header: 'pb-0',
  wrapper: 'flex-wrap',
  base: 'bg-base-80 flex flex-col h-auto max-h-[80vh] overflow-hidden',
  footer: 'flex flex-col gap-4',
  closeButton: 'text-hl top-2 right-2.5',
};

const TABS_CLASSNAMES = {
  tabList: 'bg-base-50 w-60 p-0 gap-0',
  tab: 'text-s-sb',
  tabContent: 'text-base-5 group-data-[selected=true]:text-white',
  cursor: 'bg-base-5 text-white',
  panel: 'flex flex-col flex-1 min-h-0 h-full',
};

type AttachmentsModalProps = {
  files: CaseFile[];
  links: CaseLinkType[];
  onOpenChange: () => void;
  fileUpload: AttachmentsFileUpload;
  linkMutations: AttachmentsLinkMutations;
  disabled?: boolean;
  showUploadBtn?: boolean;
  showDownloadBtn?: boolean;
  showDeleteBtn?: boolean;
  showEditBtn?: boolean;
  showPreviewBtn?: boolean;
  restrictRedactedFileDelete?: boolean;
  showRedactedMark?: boolean;
  hintLabel?: string;
  hintText?: string;
  requireRedactionConfirmation?: boolean;
  showRedactionInstructions?: boolean;
};

export const AttachmentsModal = ({
  onOpenChange,
  links,
  files,
  fileUpload,
  linkMutations,
  disabled,
  showUploadBtn,
  showDownloadBtn,
  showDeleteBtn,
  showEditBtn,
  showPreviewBtn,
  restrictRedactedFileDelete,
  showRedactedMark,
  hintLabel,
  hintText,
  requireRedactionConfirmation,
  showRedactionInstructions,
}: AttachmentsModalProps) => {
  return (
    <Modal isOpen onOpenChange={onOpenChange} size="4xl" classNames={MODAL_CLASSNAMES}>
      <ModalContent>
        <Form<AttachmentsForm> defaultValues={{ links }} schema={linksCaseSchema} onSubmit={() => {}}>
          <ModalHeader className="flex flex-col gap-4">
            <h2 className="text-base-20">Attachments & Links</h2>
            <Divider />
          </ModalHeader>
          <ModalBody className="py-6 flex-1 min-h-0 overflow-y-auto">
            <Tabs aria-label="Attachment types" classNames={TABS_CLASSNAMES} radius="full">
              <Tab key="files" title="Files">
                <div className="flex h-[60vh] min-h-0 flex-col">
                  <FilesList
                    files={files}
                    isUploading={fileUpload.isUploading}
                    onFileUpload={fileUpload.onFileUpload}
                    onDeleteFile={fileUpload.onDeleteFile}
                    disabled={disabled}
                    showUploadBtn={showUploadBtn}
                    showDownloadBtn={showDownloadBtn}
                    showDeleteBtn={showDeleteBtn}
                    showPreviewBtn={showPreviewBtn}
                    restrictRedactedFileDelete={restrictRedactedFileDelete}
                    showRedactedMark={showRedactedMark}
                    hintLabel={hintLabel}
                    hintText={hintText}
                    requireRedactionConfirmation={requireRedactionConfirmation}
                    showRedactionInstructions={showRedactionInstructions}
                  />
                </div>
              </Tab>
              <Tab key="links" title="Links">
                <div className="flex h-[60vh] min-h-0 flex-col">
                  <LinksList
                    initialLinks={links}
                    addLink={linkMutations.addLink}
                    editLink={linkMutations.editLink}
                    deleteLink={linkMutations.deleteLink}
                    isPending={linkMutations.isPending}
                    disabled={disabled}
                    showUploadBtn={showUploadBtn}
                    showDeleteBtn={showDeleteBtn}
                    showEditBtn={showEditBtn}
                  />
                </div>
              </Tab>
            </Tabs>
          </ModalBody>
        </Form>
      </ModalContent>
    </Modal>
  );
};
